// ── EVIDENCE P1.2 — the words for provisioning outcomes, defined once ───────────────────────────
//
// ⚠ The api sends a readable `reason` for every skip and failure; the surface shows it. These labels
//   are only the short HEADINGS a reason is grouped under — no component spells them itself.

import type { ProvisioningReasonCode, ProvisioningRunState } from '@/api/types/evidence-provisioning.types';

export const PROVISIONING_ACTION = 'Provision evidence location';

export const RUN_STATE_LABEL: Record<ProvisioningRunState, string> = {
  queued: 'Queued',
  running: 'In progress',
  completed: 'Completed',
  completed_with_failures: 'Completed with failures',
};

// ⚠ Box GA S3 (item 10) — PROVIDER-NEUTRAL. Bulk provisioning now runs for Box as well as SharePoint,
//   and these headings are shared by both: "Microsoft 365 error" over a Box failure, or "this library"
//   for a Box folder, would send a tenant to the wrong console. The provider-specific detail is in each
//   item's server-written `reason`.
export const REASON_LABEL: Record<ProvisioningReasonCode, string> = {
  already_provisioned: 'Already provisioned',
  guidance_protected: 'Guidance file could not be updated',
  access_denied: 'VeriGRC does not have write access to this evidence repository',
  library_not_found: 'Evidence repository not reachable',
  binding_not_validated: 'Evidence repository not validated',
  platform_app_not_provisioned: 'Provider connection not set up',
  control_not_found: 'Control no longer available',
  provider_error: 'Storage provider error',
};

export const REJECTED_LABEL = {
  not_in_workspace: 'not in this workspace',
  control_not_found: 'not a control this workspace can see',
} as const;

/** The server's first error message, or a fallback that still says what failed. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { errors?: { message?: string }[] } } };
  return e?.response?.data?.errors?.[0]?.message ?? fallback;
}
